import { DECK_THEME as T, fmt$, fmtPct, fmtX, fmtNum } from "../../lib/deckModel";
import { MarketLeasing } from "./MarketLeasing";
import { Tag, Card, CardTitle, SourceLine, Metric, Bars, imgSrc } from "./DeckUI";
import { PropertySite } from "./PropertySite";
import { WatchButton } from "./HeroVideo";
import { Editable, EditPanelButton, useDeckEdit } from "./EditCtx";
import { MarketEditor } from "./MarketEditor";
import { SiteEditor } from "./PropertySite";
import { ExitsPanel } from "./BuilderPanels";
import { RevenuePage, BudgetPage } from "./MurrietaPages";
import { ValuationPage } from "./ValuationPage";

export { Tag, Card, CardTitle, SourceLine, Metric, Bars, imgSrc };

const Rows = ({ rows, total, testid }) => (
  <Card className="!p-0 overflow-hidden" testid={testid}>
    {rows.map(([k, v, bold]) => (
      <div key={k} className={`flex justify-between gap-4 px-6 py-3.5 text-sm border-b last:border-b-0 ${bold ? "font-semibold" : ""}`} style={{ borderColor: T.neutral, color: bold ? T.navy : T.charcoal }}>
        <span>{k}</span><span className="tabular-nums">{v}</span>
      </div>
    ))}
    {total && <div className="flex justify-between px-6 py-4 text-white" style={{ background: T.navy }}><span className="text-sm">{total[0]}</span><span className="tabular-nums text-lg" style={{ fontFamily: T.serif }}>{total[1]}</span></div>}
  </Card>
);

const Facts = ({ project, a }) => {
  const rows = [["Address", project.address], ["City / State", [project.city, project.state].filter(Boolean).join(", ")],
    ["County", project.county], ["APN", project.apn], ["Ownership", project.owner],
    ["Acreage", project.acreage || a.site_acreage], ["Rentable SF", a.rentable_sf && fmtNum(a.rentable_sf)],
    ["Parking", a.parking_count && `${fmtNum(a.parking_count)} spaces`], ["Project stage", project.stage]].filter(([, v]) => v);
  return <Rows rows={rows} testid="deck-facts" />;
};

function ExitLadder({ exits, only }) {
  const list = only ? exits.filter((e) => e.key === only) : exits;
  if (!list.length) return <p className="text-sm" style={{ color: T.muted }}>No exits enabled.</p>;
  return (
    <Card className="!p-0 overflow-x-auto" testid="deck-exit-ladder">
      <table className="w-full text-sm min-w-[880px]">
        <thead><tr className="text-left text-[10px] uppercase tracking-[0.16em]" style={{ color: T.muted, fontFamily: T.mono, background: T.warm }}>
          {["Exit", "Cumulative cost", "Remaining", "Exit value", "Gross profit", "Net profit", "Profit on cost", "Multiple", "IRR", "Risk"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}
        </tr></thead>
        <tbody>
          {list.map((e, i) => (
            <tr key={e.key} className="border-t align-top" style={{ borderColor: T.neutral, color: T.charcoal }} data-testid={`deck-exit-${e.key}`}>
              <td className="px-4 py-3.5"><div style={{ color: T.navy, fontFamily: T.serif }} className="text-base">{String(i + 1).padStart(2, "0")} · {e.label}</div>
                <div className="text-xs mt-1" style={{ color: T.muted }}>Sold: {e.sold || "—"}<br />Retained: {e.retained || "—"}</div></td>
              <td className="px-4 py-3.5 tabular-nums">{fmt$(e.cost)}</td>
              <td className="px-4 py-3.5 tabular-nums">{fmt$(e.remaining)}</td>
              <td className="px-4 py-3.5 tabular-nums font-semibold" style={{ color: T.navy }}>{fmt$(e.value)}</td>
              <td className="px-4 py-3.5 tabular-nums">{e.ready ? fmt$(e.gross) : "—"}</td>
              <td className="px-4 py-3.5 tabular-nums" style={{ color: T.accent }}>{e.ready ? fmt$(e.net) : "—"}</td>
              <td className="px-4 py-3.5 tabular-nums">{fmtPct(e.poc)}</td>
              <td className="px-4 py-3.5 tabular-nums">{fmtX(e.em)}</td>
              <td className="px-4 py-3.5 tabular-nums">{fmtPct(e.irr)}</td>
              <td className="px-4 py-3.5">{e.risk || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

const grid = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4";

const BODY = {
  investment_overview: ({ m }) => (
    <div className={grid}>
      <Metric label="Total development cost" value={fmt$(m.totalCost)} tag="underwriting" testid="deck-m-total-cost" />
      <Metric label="Stabilized NOI" value={fmt$(m.noi)} testid="deck-m-noi" />
      <Metric label="Stabilized value" value={fmt$(m.value)} sub={m.cap ? `at ${fmtPct(m.cap, 2)} cap` : ""} testid="deck-m-value" />
      <Metric label="Yield on cost" value={fmtPct(m.yoc, 2)} sub={`Spread ${fmt$(m.devSpread)}`} accent testid="deck-m-yoc" />
    </div>
  ),
  property_overview: ({ deck, section }) => (section.content?.site ? <PropertySite site={section.content.site} project={deck.project || {}} /> : <Facts project={deck.project || {}} a={deck.assumptions || {}} />),
  site_feasibility: ({ deck, section }) => (section.content?.site ? <PropertySite site={section.content.site} project={deck.project || {}} /> : <Facts project={deck.project || {}} a={deck.assumptions || {}} />),
  market_leasing: ({ section, m, deck }) => <MarketLeasing data={section.content?.market} model={m} assumptions={deck.assumptions || {}} />,
  revenue_noi: (p) => <RevenuePage {...p} />,
  development_budget: (p) => <BudgetPage {...p} />,
  sources_uses: ({ m }) => (
    <div className="grid lg:grid-cols-2 gap-6">
      <Rows rows={[["Construction debt", fmt$(m.debt, false)], ["Equity", fmt$(m.equity, false)]]} total={["Total sources", fmt$(m.debt + m.equity, false)]} />
      <Rows rows={m.budget.map(([k, v]) => [k, fmt$(v, false)])} total={["Total uses", fmt$(m.totalCost, false)]} />
    </div>
  ),
  valuation: (p) => <ValuationPage {...p} />,
  capital_stack: ({ m }) => {
    const tot = m.debt + m.equity || 1;
    return (
      <div className="grid lg:grid-cols-[220px_1fr] gap-6 items-stretch" data-testid="deck-capital-stack">
        <div className="rounded-2xl overflow-hidden flex flex-col min-h-[260px]">
          <div className="flex items-center justify-center text-white text-sm" style={{ flex: m.equity / tot || 0.001, background: T.accent, fontFamily: T.mono }}>EQUITY {fmtPct(m.equity / tot, 0)}</div>
          <div className="flex items-center justify-center text-white text-sm" style={{ flex: m.debt / tot || 0.001, background: T.navy, fontFamily: T.mono }}>DEBT {fmtPct(m.debt / tot, 0)}</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Metric label="Construction debt" value={fmt$(m.debt)} /><Metric label="Equity requirement" value={fmt$(m.equity)} accent />
          <Metric label="Refinance proceeds" value={fmt$(m.refiProceeds)} /><Metric label="Cash-out after debt payoff" value={fmt$(m.cashOut)} />
        </div>
      </div>
    );
  },
  cap_rate_selection: (p) => <ValuationPage {...p} variant="cap_rate" />,
  exit_strategy: ({ m }) => <ExitLadder exits={m.exits} />,
  value_ladder: ({ m }) => <ExitLadder exits={m.exits} />,
  build_sell: ({ m }) => <ExitLadder exits={m.exits} only="build_sell" />,
  build_hold: ({ m }) => <ExitLadder exits={m.exits} only="build_hold" />,
};

const DATA_PANELS = {
  market_leasing: ["Edit market data", (c, set) => <MarketEditor market={c.market} onChange={(market) => set({ market })} />],
  property_overview: ["Edit site data", (c, set) => <SiteEditor site={c.site} onChange={(site) => set({ site })} />],
  site_feasibility: ["Edit site data", (c, set) => <SiteEditor site={c.site} onChange={(site) => set({ site })} />],
};
const EXIT_TYPES = ["exit_strategy", "value_ladder", "build_sell", "build_hold"];

export function SectionContent({ section, deck, model }) {
  const { editing, setContent, setExits } = useDeckEdit();
  const B = BODY[section.type];
  const c = section.content || {};
  const set = (p) => setContent?.(section.id, p);
  const bullets = c.bullets || [];
  const shown = editing ? bullets : bullets.filter((b) => String(b).trim());
  const panel = DATA_PANELS[section.type];
  return (
    <div className="space-y-10">
      {(c.body || editing) && <Editable as="p" value={c.body} onChange={(v) => set({ body: v })} placeholder="Lead paragraph" className="block text-xl sm:text-2xl leading-snug max-w-4xl whitespace-pre-line" style={{ color: T.navy, fontFamily: T.serif }} testid="deck-section-body" />}
      {editing && (panel || EXIT_TYPES.includes(section.type)) && (
        <div>{panel ? <EditPanelButton label={panel[0]} testid="deck-edit-data">{panel[1](c, set)}</EditPanelButton>
          : <EditPanelButton label="Edit exits" testid="deck-edit-exits"><ExitsPanel exits={deck.exits || []} model={model} onChange={setExits} /></EditPanelButton>}</div>
      )}
      {B && <B deck={deck} m={model} section={section} />}
      {shown.length > 0 && (
        <ul className="grid sm:grid-cols-2 gap-3">
          {shown.map((b, i) => (
            <li key={i} className="flex gap-3 rounded-xl border bg-white px-5 py-4 text-sm" style={{ borderColor: T.line, color: T.charcoal }}>
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: T.accent }} />
              <Editable as="span" value={b} onChange={(v) => set({ bullets: v ? bullets.map((x, j) => (j === i ? v : x)) : bullets.filter((_, j) => j !== i) })} placeholder="Key point" className="flex-1" testid={`deck-bullet-text-${i}`} />
            </li>
          ))}
        </ul>
      )}
      {editing && <button onClick={() => set({ bullets: [...bullets, "New key point"] })} className="text-xs font-medium" style={{ color: T.accent }} data-testid="deck-add-bullet-inline">+ Add key point</button>}
      {!B && !c.body && !shown.length && !editing && <p className="text-sm italic" style={{ color: T.muted }}>Content for this section hasn't been added yet.</p>}
    </div>
  );
}

export const Eyebrow = ({ n, title }) => (
  <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.25em]" style={{ color: T.muted, fontFamily: T.mono }}>
    <span style={{ color: T.navy }}>{String(n).padStart(2, "0")}</span><span className="h-px w-10" style={{ background: T.line }} /><span>{title}</span>
  </div>
);

export function SectionHeader({ section, index }) {
  const { setContent, setSection } = useDeckEdit();
  const c = section.content || {};
  return (
    <div className="mb-12">
      <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.25em]" style={{ color: T.muted, fontFamily: T.mono }}>
        <span style={{ color: T.navy }}>{String(index + 1).padStart(2, "0")}</span><span className="h-px w-10" style={{ background: T.line }} />
        <Editable value={section.title} onChange={(v) => v && setSection?.(section.id, { title: v })} testid="deck-section-eyebrow" />
      </div>
      <Editable as="h2" value={c.headline || section.title} onChange={(v) => setContent?.(section.id, { headline: v })} className="mt-5 block text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-5xl" style={{ color: T.navy, fontFamily: T.serif }} testid="deck-section-headline" />
      <Editable as="p" value={c.subtitle} onChange={(v) => setContent?.(section.id, { subtitle: v })} placeholder="Subtitle / lede" className="mt-6 block text-base sm:text-lg max-w-3xl" style={{ color: T.muted }} testid="deck-section-subtitle" />
    </div>
  );
}

export function DeckSection({ section, index, deck, model }) {
  return (
    <section id={`sec-${section.id}`} className="px-6 sm:px-12 lg:px-[8%] py-14 sm:py-20" style={{ background: T.warm, fontFamily: T.sans }} data-testid={`deck-section-${section.type}`}>
      <SectionHeader section={section} index={index} />
      <SectionContent section={section} deck={deck} model={model} />
    </section>
  );
}


export function DeckHero({ deck, model, compact }) {
  const p = deck.project || {};
  return (
    <header className={`relative overflow-hidden ${compact ? "min-h-[440px]" : "min-h-[78vh]"} flex items-center`} style={{ background: T.navy, fontFamily: T.sans }} data-testid="deck-hero">
      {p.cover_image && <img src={imgSrc(p.cover_image)} alt="" className="absolute inset-0 h-full w-full object-cover" />}
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(24,54,80,0.88) 0%, rgba(24,54,80,0.55) 55%, rgba(24,54,80,0.25) 100%)" }} />
      <div className="relative w-full px-6 sm:px-12 lg:px-[8%] py-20">
        <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.3em] text-white/60" style={{ fontFamily: T.mono }}>ARQITEK<span className="h-px w-12 bg-white/30" />{[p.project_type, [p.city, p.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</div>
        <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white max-w-4xl" style={{ fontFamily: T.serif }} data-testid="deck-hero-title">{deck.name}</h1>
        {p.tagline && <p className="mt-6 text-lg sm:text-xl text-white/80 max-w-2xl">{p.tagline}</p>}
        <WatchButton video={p.video} />
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden max-w-4xl" style={{ background: "rgba(255,255,255,0.14)" }}>
          {[["Development cost", fmt$(model.totalCost)], ["Stabilized NOI", fmt$(model.noi)], ["Stabilized value", fmt$(model.value)], ["Yield on cost", fmtPct(model.yoc, 2)]].map(([k, v]) => (
            <div key={k} className="px-5 py-4 backdrop-blur-md" style={{ background: "rgba(24,54,80,0.55)" }}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60" style={{ fontFamily: T.mono }}>{k}</div>
              <div className="mt-1.5 text-2xl text-white tabular-nums" style={{ fontFamily: T.serif }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    </header>
  );
}
